import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import InfoTooltip from 'components/InfoTooltip';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import SubSection from 'components/section-menu/SubSection';
import { ReactNode } from 'react';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { ContactFormState } from './useContactForm';

/**
 * One titled group of the compliance fields. A record section has no groups: each field there is a cell of the
 * card's grid, where the read view has its row.
 */
const Group = ({ title, children }: { title: string; children: ReactNode }) =>
    useInRecordSection() ? <>{children}</> : <SubSection title={title}>{children}</SubSection>;

/** Compliance: verification, risk assessment and exemptions. */
export const ComplianceFields = ({ form }: { form: ContactFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers, hasUpdateCompliancePermission, partyType, relationshipType } = form;

    return (
        <>
            <Group title="contacts.compliance.verification">
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contacts.verification-status"
                    placeholder="contacts.verification-status"
                    name="complianceVerificationStatusTypeCode"
                    tooltip={<InfoTooltip text="contacts.tooltips.verification-status" />}
                    options={
                        getValidOptionsFromClassifier<string, string>(
                            classifiers['COMPLIANCE_VERIFICATION_STATUS_TYPE']
                        ) ?? []
                    }
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                    readonly={!hasUpdateCompliancePermission}
                />
                <TextAreaInput
                    name="complianceVerificationNotes"
                    control={control}
                    label="contacts.verification-notes"
                    enableAutofill
                    tooltip={<InfoTooltip text="contacts.tooltips.verification-notes" />}
                    readonly={!hasUpdateCompliancePermission}
                />
            </Group>
            <Group title="contacts.compliance.risk-assessment">
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contacts.risk-profile"
                    placeholder="contacts.risk-profile"
                    tooltip={<InfoTooltip text="contacts.tooltips.risk-profile" />}
                    name="riskProfileTypeCode"
                    options={getValidOptionsFromClassifier<string, string>(classifiers['RISK_PROFILE_TYPE']) ?? []}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                    readonly={!hasUpdateCompliancePermission}
                />
                <TextAreaInput
                    name="riskProfileNotes"
                    control={control}
                    label="contacts.risk-profile-notes"
                    enableAutofill
                    tooltip={<InfoTooltip text="contacts.tooltips.risk-profile-notes" />}
                    readonly={!hasUpdateCompliancePermission}
                />
                <MultiSelectInput
                    control={control}
                    name="riskFactors"
                    label="contacts.risk-factors"
                    placeholder="contacts.risk-factors"
                    tooltip={<InfoTooltip text="contacts.tooltips.risk-factors" />}
                    options={
                        (getValidOptionsFromClassifier(classifiers['RISK_FACTOR_TYPE_CODE']).filter(
                            (option) => option.parameters?.['isAutomatic'] !== true
                        ) as {
                            label: string;
                            value: string | number;
                        }[]) ?? []
                    }
                    setValue={setValue}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                />
            </Group>
            <Group title="contacts.compliance.exemptions">
                <CheckboxInput
                    control={control}
                    name="isServiceAgreementNotNeeded"
                    label="contacts.service-agreement-not-needed"
                    tooltip={<InfoTooltip text="contacts.tooltips.is-service-agreement-not-needed" />}
                    readonly={!hasUpdateCompliancePermission}
                />
                {partyType === PartyType.COMPANY && relationshipType === RelationshipTypeCode.CLIENT ? (
                    <CheckboxInput
                        control={control}
                        name="isUboNotNeeded"
                        label="contacts.ubo-not-needed"
                        tooltip={<InfoTooltip text="contacts.tooltips.ubo-not-needed" />}
                        readonly={!hasUpdateCompliancePermission}
                    />
                ) : null}
                <CheckboxInput
                    control={control}
                    name="isPersonaNonGrata"
                    tooltip={<InfoTooltip text="contacts.tooltips.persona-non-grata" />}
                    label="contacts.persona-non-grata"
                />
            </Group>
        </>
    );
};
